import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { ArrowLeftIcon, ArrowRightIcon } from '../components/icons/NavIcons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PageHeader from '../components/PageHeader';

const EventStatsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [totalVisits, setTotalVisits] = useState(0);
  const [uniqueVisits, setUniqueVisits] = useState(0);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchChartData();
    }
  }, [eventId, timeRange]);

  const fetchEvent = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          category:category_id (name, icon, color_code),
          age_group:age_group_id (name, icon)
        `)
        .eq('id', eventId)
        .single();

      if (error) throw error;
      setEvent(data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'événement:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    setLoadingChart(true);
    try {
      const now = new Date();
      let startDate;
      let groupFormat = 'hour';
      
      switch (timeRange) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          groupFormat = 'hour';
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          groupFormat = 'day';
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          groupFormat = 'day';
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          groupFormat = 'day';
          break;
        default:
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          groupFormat = 'day';
      }

      // Récupérer toutes les visites pour cet événement
      const { data: allData, error } = await supabase
        .from('visits')
        .select('visited_at, visitor_id')
        .eq('event_id', eventId)
        .gte('visited_at', startDate.toISOString())
        .lte('visited_at', now.toISOString())
        .order('visited_at', { ascending: true });

      if (error) throw error;

      // Initialiser toutes les dates de la plage
      const grouped = new Map();
      const iterDate = new Date(startDate);
      
      if (groupFormat === 'day') {
        iterDate.setHours(0, 0, 0, 0);
      } else {
        iterDate.setMinutes(0, 0, 0);
      }
      
      const endDate = new Date(now);
      if (groupFormat === 'day') {
        endDate.setHours(23, 59, 59, 999);
      }
      
      while (iterDate <= endDate) {
        const timestamp = iterDate.getTime();
        let displayKey;
        
        if (groupFormat === 'hour') {
          displayKey = `${String(iterDate.getDate()).padStart(2, '0')}/${String(iterDate.getMonth() + 1).padStart(2, '0')} ${String(iterDate.getHours()).padStart(2, '0')}h`;
        } else {
          displayKey = `${String(iterDate.getDate()).padStart(2, '0')}/${String(iterDate.getMonth() + 1).padStart(2, '0')}`;
        }
        
        grouped.set(timestamp, {
          timestamp,
          displayKey,
          total: 0,
          uniqueVisitors: new Set()
        });

        if (groupFormat === 'hour') {
          iterDate.setTime(iterDate.getTime() + 60 * 60 * 1000);
        } else {
          iterDate.setTime(iterDate.getTime() + 24 * 60 * 60 * 1000);
        }
      }

      // Remplir avec les données réelles
      let totalCount = 0;
      const uniqueVisitorsSet = new Set();

      allData.forEach(visit => {
        totalCount++;
        if (visit.visitor_id) {
          uniqueVisitorsSet.add(visit.visitor_id);
        }

        const date = new Date(visit.visited_at);
        let timestamp;
        
        if (groupFormat === 'hour') {
          const hourDate = new Date(date);
          hourDate.setMinutes(0, 0, 0);
          timestamp = hourDate.getTime();
        } else {
          const dayDate = new Date(date);
          dayDate.setHours(0, 0, 0, 0);
          timestamp = dayDate.getTime();
        }

        const bucket = grouped.get(timestamp);
        if (bucket) {
          bucket.total++;
          if (visit.visitor_id) {
            bucket.uniqueVisitors.add(visit.visitor_id);
          }
        }
      });

      setTotalVisits(totalCount);
      setUniqueVisits(uniqueVisitorsSet.size);

      // Convertir en tableau trié
      const chartArray = Array.from(grouped.values())
        .sort((a, b) => a.timestamp - b.timestamp)
        .map(item => ({
          date: item.displayKey,
          [t('eventStats.totalVisits')]: item.total,
          [t('eventStats.uniqueVisits')]: item.uniqueVisitors.size
        }));

      setChartData(chartArray);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2894D8]"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">{t('eventStats.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <PageHeader title={t('eventStats.title')} />

      <div className="p-4">
        {/* Statistiques générales */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <h2 className="text-lg font-semibold mb-4">{t('eventStats.overview')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">{t('eventStats.totalVisits')}</p>
              <p className="text-3xl font-bold text-[#2894D8]">{totalVisits}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-xs text-gray-600 mb-1">{t('eventStats.uniqueVisits')}</p>
              <p className="text-3xl font-bold text-[#10B981]">{uniqueVisits}</p>
            </div>
          </div>
        </div>

        {/* Graphique */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
          <h2 className="text-lg font-semibold mb-4">{t('eventStats.chartTitle')}</h2>
          
          {/* Filtres temporels */}
          <div className="flex gap-2 overflow-x-auto mb-4">
            <button
              onClick={() => setTimeRange('24h')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '24h'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('eventStats.24h')}
            </button>
            <button
              onClick={() => setTimeRange('7d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '7d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('eventStats.7d')}
            </button>
            <button
              onClick={() => setTimeRange('30d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '30d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('eventStats.30d')}
            </button>
            <button
              onClick={() => setTimeRange('90d')}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                timeRange === '90d'
                  ? 'bg-[#2894D8] text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {t('eventStats.90d')}
            </button>
          </div>

          {/* Graphique */}
          {loadingChart ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2894D8]"></div>
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-64 flex items-center justify-center">
              <p className="text-gray-500">{t('eventStats.noData')}</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey={t('eventStats.totalVisits')}
                  stroke="#2894D8" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line 
                  type="monotone" 
                  dataKey={t('eventStats.uniqueVisits')}
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Bouton vers la page de détail */}
        <button
          onClick={() => navigate(`/event/${eventId}`)}
          className="w-full bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            {/* <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">{event.category?.icon}</span>
            </div> */}
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900">{t('eventStats.viewEventDetails')}</p>
              <p className="text-sm text-gray-600 truncate">{event.title}</p>
            </div>
          </div>
          <ArrowRightIcon className="w-5 h-5 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

export default EventStatsPage;
